import React, { useState, useEffect } from "react";
import config from "../../Common/Configurations/APIConfig";
import apiService from "../../Common/Configurations/APIService";
import { useAuth } from "../../Common/Configurations/AuthContext";
import Modal from "react-bootstrap/Modal";
import currencyFlags from "../../Data/CurrencyFlags.json";

const initialFieldValues = {
  customerId: "00000000-0000-0000-0000-000000000000",
  companyId: "00000000-0000-0000-0000-000000000000",
  customerName: "",
  phoneNumber: "",
  email: "",
  companyName: "",
  address: "",
  gstNo: "",
  panNo: "",
  city: "",
  state: "",
  country: "",
};

const CustomerFormModal = ({
  show,
  onHide,
  recordForEdit,
  onSuccess,
  mode = "add",
}) => {
  const { user } = useAuth();
  const [btnLoading, setBtnLoading] = useState(false);
  const [values, setValues] = useState(initialFieldValues);
  const [errors, setErrors] = useState({});
  const [countries, setCountries] = useState([]);

  useEffect(() => {
    if (currencyFlags && Array.isArray(currencyFlags)) {
      const sortedCountries = [...currencyFlags].sort((a, b) =>
        a.country.localeCompare(b.country)
      );
      setCountries(sortedCountries);
    }
  }, []);

  const getUserDefaultCurrency = () => {
    return user?.currency || "INR";
  };

  const getUserDefaultCountry = () => {
    return user?.country || "IN";
  };

  const getInitialValues = () => {
    return {
      ...initialFieldValues,
      companyId: user?.cid || "00000000-0000-0000-0000-000000000000",
      country: getUserDefaultCountry(),
    };
  };

  useEffect(() => {
    if (recordForEdit) {
      setValues(recordForEdit);
    } else {
      setValues(getInitialValues());
    }
  }, [recordForEdit, show, user]);

  const validate = () => {
    let temp = {};
    temp.customerName = values.customerName?.trim() ? true : false;
    temp.phoneNumber = values.phoneNumber?.trim() ? true : false;
    temp.email = values.email
      ? /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)
      : true;

    setErrors(temp);
    return Object.values(temp).every((x) => x === true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validate()) {
      setBtnLoading(true);

      try {
        // Prepare form data - ensure no null values for required fields
        var formData = {
          customerId: values.customerId,
          companyId: values.companyId,
          customerName: values.customerName.trim(),
          phoneNumber: values.phoneNumber.trim(),
          email: values.email?.trim() || "", // Empty string instead of null
          companyName: values.companyName?.trim() || "",
          address: values.address?.trim() || "",
          gstNo: values.gstNo?.trim() || "",
          panNo: values.panNo?.trim() || "",
          city: values.city?.trim() || "", // Empty string instead of null
          state: values.state?.trim() || "", // Empty string instead of null
          country: values.country?.trim() || "", // Empty string instead of null
        };

        console.log("Sending customer data:", formData);

        const isNew =
          formData.customerId === "00000000-0000-0000-0000-000000000000";

        if (isNew) {
          // Remove customerId for new records
          const { customerId, ...createData } = formData;
          await apiService.create(config.CREATECUSTOMER, createData);
        } else {
          await apiService.update(config.UPDATECUSTOMER, formData);
        }

        resetForm();
        if (onSuccess) {
          onSuccess();
        }
      } catch (error) {
        console.error("Error saving customer:", error);

        let errorMessage = "Failed to save customer";
        if (error.response?.data) {
          errorMessage =
            typeof error.response.data === "string"
              ? error.response.data
              : JSON.stringify(error.response.data);
        } else if (error.message) {
          errorMessage = error.message;
        }

        alert(`Error: ${errorMessage}`);
      } finally {
        setBtnLoading(false);
      }
    }
  };
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const fieldsToUppercase = ["customerName", "gstNo"];

    if (name === "country") {
      const selectedCountry = countries.find(
        (country) => country.countryCode === value
      );
      setValues({
        ...values,
        country: value,
      });
    } else {
      setValues({
        ...values,
        [name]: fieldsToUppercase.includes(name) ? value.toUpperCase() : value,
      });
    }
  };

  const resetForm = () => {
    setValues(getInitialValues());
    setErrors({});
    onHide();
  };

  const applyErrorClass = (field) =>
    field in errors && errors[field] === false ? " form-control-danger" : "";

  const getModalTitle = () => {
    if (mode === "edit") return "Edit Customer";
    return values.customerId === "00000000-0000-0000-0000-000000000000"
      ? "Add Customer"
      : "Edit Customer";
  };

  const isDefaultCountry = (countryCode) => {
    return countryCode === getUserDefaultCountry();
  };

  return (
    <Modal show={show} onHide={resetForm} size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>{getModalTitle()}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <form onSubmit={handleSubmit} autoComplete="off" noValidate>
          <div className="row">
            {/* Row 1: Customer Name, Phone Number, Email */}
            <div className="col-lg-4 mb-3">
              <label htmlFor="customerName" className="form-label">
                Customer Name *
              </label>
              <input
                type="text"
                value={values.customerName || ""}
                name="customerName"
                onChange={handleInputChange}
                className={"form-control" + applyErrorClass("customerName")}
                placeholder="Enter Customer Name"
                maxLength={50}
                disabled={btnLoading}
              />
              {errors.customerName === false && (
                <div className="text-danger small mt-1">
                  Customer name is required
                </div>
              )}
            </div>

            <div className="col-lg-4 mb-3">
              <label htmlFor="phoneNumber" className="form-label">
                Phone Number *
              </label>
              <input
                type="tel"
                value={values.phoneNumber || ""}
                name="phoneNumber"
                onChange={handleInputChange}
                className={"form-control" + applyErrorClass("phoneNumber")}
                placeholder="Enter Phone Number"
                maxLength={15}
                disabled={btnLoading}
              />
              {errors.phoneNumber === false && (
                <div className="text-danger small mt-1">
                  Phone number is required
                </div>
              )}
            </div>

            <div className="col-lg-4 mb-3">
              <label htmlFor="email" className="form-label">
                Email
              </label>
              <input
                type="email"
                value={values.email || ""}
                name="email"
                onChange={handleInputChange}
                className={
                  "form-control" +
                  (errors.email === false ? " form-control-danger" : "")
                }
                placeholder="Enter Email"
                maxLength={50}
                disabled={btnLoading}
              />
              {errors.email === false && (
                <div className="text-danger small mt-1">
                  Please enter a valid email address
                </div>
              )}
            </div>

            {/* Company Name */}
            <div className="col-lg-8 mb-3">
              <label htmlFor="companyName" className="form-label">
                Company Name
              </label>
              <input
                type="text"
                value={values.companyName || ""}
                name="companyName"
                onChange={handleInputChange}
                className="form-control"
                placeholder="Enter Company Name"
                maxLength={100}
                disabled={btnLoading}
              />
            </div>

            {/* Address */}
            <div className="col-lg-8 mb-3">
              <label htmlFor="address" className="form-label">
                Address
              </label>
              <input
                type="text"
                value={values.address || ""}
                name="address"
                onChange={handleInputChange}
                className="form-control"
                placeholder="Enter Address"
                maxLength={200}
                disabled={btnLoading}
              />
            </div>

            {/* City */}
            <div className="col-lg-4 mb-3">
              <label htmlFor="city" className="form-label">
                City
              </label>
              <input
                type="text"
                value={values.city || ""}
                name="city"
                onChange={handleInputChange}
                className="form-control"
                placeholder="Enter City"
                maxLength={50}
                disabled={btnLoading}
              />
            </div>

            {/* State */}
            <div className="col-lg-4 mb-3">
              <label htmlFor="state" className="form-label">
                State
              </label>
              <input
                type="text"
                value={values.state || ""}
                name="state"
                onChange={handleInputChange}
                className="form-control"
                placeholder="Enter State"
                maxLength={50}
                disabled={btnLoading}
              />
            </div>

            {/* Country */}
            <div className="col-lg-4 mb-3">
              <label htmlFor="country" className="form-label">
                Country *
              </label>
              <select
                name="country"
                value={values.country || ""}
                onChange={handleInputChange}
                className="form-select"
                disabled={btnLoading}
                required
              >
                <option value="">Select Country</option>
                {countries.map((country) => (
                  <option key={country.countryCode} value={country.countryCode}>
                    {country.flag && (
                      <img
                        src={country.flag}
                        alt={country.country}
                        style={{
                          width: "20px",
                          height: "15px",
                          marginRight: "8px",
                          verticalAlign: "middle",
                        }}
                      />
                    )}
                    {country.country} - {country.countryCode}
                    {isDefaultCountry(country.countryCode) && " ★"}
                  </option>
                ))}
              </select>
            </div>

            {/* PAN Number */}
            <div className="col-lg-4 mb-3">
              <label htmlFor="panNo" className="form-label">
                PAN Number
              </label>
              <input
                type="text"
                value={values.panNo || ""}
                name="panNo"
                onChange={handleInputChange}
                className="form-control"
                placeholder="Enter PAN Number"
                maxLength={20}
                disabled={btnLoading}
              />
            </div>

            {/* GST No */}
            <div className="col-lg-4 mb-3">
              <label htmlFor="gstNo" className="form-label">
                GST No
              </label>
              <input
                type="text"
                value={values.gstNo || ""}
                name="gstNo"
                onChange={handleInputChange}
                className="form-control"
                placeholder="Enter GST No"
                maxLength={20}
                disabled={btnLoading}
              />
            </div>

            {/* Hidden field for companyId */}
            <input type="hidden" name="companyId" value={values.companyId} />
          </div>

          <Modal.Footer>
            <div className="hstack gap-2 justify-content-end">
              {btnLoading ? (
                <button type="button" className="btn btn-primary" disabled>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Please wait...
                </button>
              ) : (
                <button type="submit" className="btn btn-primary">
                  {values.customerId === "00000000-0000-0000-0000-000000000000"
                    ? "Create Customer"
                    : "Update Customer"}
                </button>
              )}
              <button
                type="button"
                className="btn btn-danger"
                onClick={resetForm}
                disabled={btnLoading}
              >
                Cancel
              </button>
            </div>
          </Modal.Footer>
        </form>
      </Modal.Body>
    </Modal>
  );
};

export default CustomerFormModal;
